import * as React from "react"
import { CheckIcon, ChevronDownIcon, XIcon } from "lucide-react"

import { cn } from "@/lib/utils"
import { Badge } from "@/components/ui/badge"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

export interface MultiSelectOption {
  label: string
  value: string
  group?: string
}

interface MultiSelectSearchableProps {
  readonly options: MultiSelectOption[]
  readonly selected: string[]
  readonly onChange: (values: string[]) => void
  readonly placeholder?: string
  readonly searchPlaceholder?: string
  readonly disabled?: boolean
  readonly className?: string
}

function MultiSelectSearchable({
  options,
  selected,
  onChange,
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  disabled = false,
  className,
}: Readonly<MultiSelectSearchableProps>) {
  const [open, setOpen] = React.useState(false)

  const hasGroups = options.some((opt) => opt.group)

  const groupedOptions = React.useMemo(() => {
    if (!hasGroups) return null
    const groups = new Map<string, MultiSelectOption[]>()
    const ungrouped: MultiSelectOption[] = []
    for (const opt of options) {
      if (opt.group) {
        const existing = groups.get(opt.group)
        if (existing) {
          existing.push(opt)
        } else {
          groups.set(opt.group, [opt])
        }
      } else {
        ungrouped.push(opt)
      }
    }
    return { groups, ungrouped }
  }, [options, hasGroups])

  const selectedLabels = selected
    .map((val) => options.find((opt) => String(opt.value) === String(val)))
    .filter(Boolean) as MultiSelectOption[]

  const handleSelect = (value: string) => {
    const strValue = String(value)
    if (selected.some((s) => String(s) === strValue)) {
      onChange(selected.filter((s) => String(s) !== strValue))
    } else {
      onChange([...selected, value])
    }
  }

  const handleRemove = (value: string, e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    onChange(selected.filter((s) => String(s) !== String(value)))
  }

  return (
    <Popover open={open} onOpenChange={disabled ? undefined : setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "border-input-multiselect data-[placeholder]:text-muted-foreground [&_svg:not([class*='text-'])]:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-neutral-50 dark:hover:bg-neutral-100 dark:border-neutral-300 flex w-full min-h-9 items-center justify-between gap-2 rounded-md border bg-transparent px-3 py-1.5 text-sm shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50",
            className
          )}
        >
          <div className="flex flex-wrap gap-1 flex-1">
            {selectedLabels.length > 0 ? (
              selectedLabels.map((opt) => (
                <Badge
                  key={opt.value}
                  variant="secondary"
                  className="gap-1 pr-1"
                >
                  <span className="max-w-[150px] truncate">{opt.label}</span>
                  {!disabled && (
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => handleRemove(opt.value, e)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.stopPropagation()
                          e.preventDefault()
                          onChange(selected.filter((s) => String(s) !== String(opt.value)))
                        }
                      }}
                      className="rounded-sm p-0.5 hover:bg-muted-foreground/20 cursor-pointer"
                    >
                      <XIcon className="size-3" />
                    </span>
                  )}
                </Badge>
              ))
            ) : (
              <span className="text-muted-foreground">{placeholder}</span>
            )}
          </div>
          <ChevronDownIcon className="size-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
      >
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>
            {hasGroups && groupedOptions ? (
              <>
                {Array.from(groupedOptions.groups.entries()).map(
                  ([groupName, groupItems], index) => (
                    <React.Fragment key={groupName}>
                      {index > 0 && <CommandSeparator />}
                      <CommandGroup heading={groupName}>
                        {groupItems.map((option) => {
                          const isSelected = selected.some(
                            (s) => String(s) === String(option.value)
                          )
                          return (
                            <CommandItem
                              key={option.value}
                              value={option.value}
                              keywords={[option.label]}
                              onSelect={() => handleSelect(option.value)}
                            >
                              <div
                                className={cn(
                                  "flex size-4 shrink-0 items-center justify-center rounded-sm border border-primary",
                                  isSelected
                                    ? "bg-primary text-primary-foreground"
                                    : "opacity-50"
                                )}
                              >
                                {isSelected && <CheckIcon className="size-3 checkbox-white" />}
                              </div>
                              <span>{option.label}</span>
                            </CommandItem>
                          )
                        })}
                      </CommandGroup>
                    </React.Fragment>
                  )
                )}
                {groupedOptions.ungrouped.length > 0 && (
                  <>
                    <CommandSeparator />
                    <CommandGroup>
                      {groupedOptions.ungrouped.map((option) => {
                        const isSelected = selected.some(
                          (s) => String(s) === String(option.value)
                        )
                        return (
                          <CommandItem
                            key={option.value}
                            value={option.value}
                            keywords={[option.label]}
                            onSelect={() => handleSelect(option.value)}
                          >
                            <div
                              className={cn(
                                "flex size-4 shrink-0 items-center justify-center rounded-sm border border-primary",
                                isSelected
                                  ? "bg-primary text-primary-foreground"
                                  : "opacity-50"
                              )}
                            >
                                    {isSelected && <CheckIcon className="size-3 checkbox-white" />}
                            </div>
                            <span>{option.label}</span>
                          </CommandItem>
                        )
                      })}
                    </CommandGroup>
                  </>
                )}
              </>
            ) : (
              <CommandGroup>
                {options.map((option) => {
                  const isSelected = selected.some(
                    (s) => String(s) === String(option.value)
                  )
                  return (
                    <CommandItem
                      key={option.value}
                      value={option.value}
                      keywords={[option.label]}
                      onSelect={() => handleSelect(option.value)}
                    >
                      <div
                        className={cn(
                          "flex size-4 shrink-0 items-center justify-center rounded-sm border border-primary",
                          isSelected
                            ? "bg-primary text-primary-foreground"
                            : "opacity-50"
                        )}
                      >
                              {isSelected && <CheckIcon className="size-3 checkbox-white" />}
                      </div>
                      <span>{option.label}</span>
                    </CommandItem>
                  )
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

export { MultiSelectSearchable }
